import React from 'react'
import Chart from 'react-google-charts'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Radio from '@material-ui/core/Radio';
import { UsagePeriod } from 'types'

interface IProps {
  data: any[]
  dataPressure: any[]
  flowRate: any
  tanktype: any
  tank: any
  historyPeriod: number
  handlePeriodChange: (period: number) => void
  handleUsagePeriodChange: (event: any) => string
  handleFocusChange: (event: any) => void
  isAdmin: boolean
  usagePeriod: UsagePeriod
  optionSelected: string
  isfromselected: boolean
  istoselected: boolean
  chartType: string
}

const chartOptions = {
  legend: {
    position: 'top',
    alignment: 'end',
    textStyle: {
      fontSize: 13,
    },
  },
  enableInteractivity: true,
  sliceVisibilityThreshold: 0,
  chartArea: {
    width: '80%',
    height: '80%',
  },
  hAxis: {
    title: 'Date',
    titleTextStyle: {
      fontSize: 13,
      fontName: 'Arial',
      bold: true,
      italic: false,
    },
    format: 'd/M',
    gridlines: {
      color: '#f5f1e6',
    },
    textStyle: {
      fontSize: 11,
    },
  },
  vAxes: {
    0: {
      title: 'Percentage',
      titleTextStyle: {
        fontSize: 13,
        fontName: 'Arial',
        bold: true,
        italic: false,
      },
      gridlines: {
        color: '#f5f1e6',
        count: 5,
      },
      textStyle: {
        fontSize: 10,
      },
      viewWindow: {
        min: 0,
        max: 120,
      },
    },
    1: {
      gridlines: {
        color: '#f5f1e6',
        count: 5,
      },
      textStyle: {
        fontSize: 10,
      },
      viewWindow: {
        min: 0,
        max: 24,
      },
    },
  },
  lineWidth: 1,
  series: {
    0: {
      targetAxisIndex: 0,
      pointSize: 0.01,
      color: '#009696',
    },
  },
}

// Pressure Chart options - Bulk
const chartOptionsPressureBulk = {
  legend: {
    position: 'top',
    alignment: 'end',
    textStyle: {
      fontSize: 13,
    },
  },
  enableInteractivity: true,
  sliceVisibilityThreshold: 0,
  chartArea: {
    width: '80%',
    height: '80%',
  },
  hAxis: {
    title: 'Date',
    titleTextStyle: {
      fontSize: 13,
      fontName: 'Arial',
      bold: true,
      italic: false,
    },
    format: 'd/M',
    gridlines: {
      color: '#f5f1e6',
    },
    textStyle: {
      fontSize: 11,
    },
  },
  vAxes: {
    0: {
      title: 'Bar',
      titleTextStyle: {
        fontSize: 13,
        fontName: 'Arial',
        bold: true,
        italic: false,
      },
      gridlines: {
        color: '#f5f1e6',
        count: 5,
      },
      textStyle: {
        fontSize: 10,
      },
      viewWindow: {
        min: 0,
        max: 50,
      },
    }
  },
  lineWidth: 1,
  series: {
    0: {
      targetAxisIndex: 0,
      pointSize: 0.01,
      color: '#009696',
    },
  },
}

// Pressure Chart options - Pack
const chartOptionsPressurePack = {
  legend: {
    position: 'top',
    alignment: 'end',
    textStyle: {
      fontSize: 13,
    },
  },
  enableInteractivity: true,
  sliceVisibilityThreshold: 0,
  chartArea: {
    width: '80%',
    height: '80%',
  },
  hAxis: {
    title: 'Date',
    titleTextStyle: {
      fontSize: 13,
      fontName: 'Arial',
      bold: true,
      italic: false,
    },
    format: 'd/M',
    gridlines: {
      color: '#f5f1e6',
    },
    textStyle: {
      fontSize: 11,
    },
  },
  vAxes: {
    0: {
      title: 'Bar',
      titleTextStyle: {
        fontSize: 13,
        fontName: 'Arial',
        bold: true,
        italic: false,
      },
      gridlines: {
        color: '#f5f1e6',
        count: 5,
      },
      textStyle: {
        fontSize: 10,
      },
      viewWindow: {
        min: 0,
        max: 500,
      },
    },
  },
  lineWidth: 1,
  series: {
    0: {
      targetAxisIndex: 0,
      pointSize: 0.01,
      color: '#009696',
    },
  },
}

const periods = [
  {
    label: '1Y',
    value: 365,
  },
  {
    label: '6M',
    value: 180,
  },
  {
    label: '1M',
    value: 30,
  },
  {
    label: '1W',
    value: 7,
  },
]

const dateString = (date: Date) => {
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hour = date.getHours()
  const min = date.getMinutes()
  const sec = date.getSeconds()
  return `${date.getFullYear()}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}T${hour < 10 ? '0' : ''}${hour}:${min < 10 ? '0' : ''}${min}`
}

const History = ({
  data,
  dataPressure,
  flowRate,
  tanktype,
  tank,
  isAdmin,
  historyPeriod,
  handlePeriodChange,
  usagePeriod,
  handleUsagePeriodChange,
  handleFocusChange,
  optionSelected,
  isfromselected,
  istoselected,
  chartType
}: IProps) => {

  if (isAdmin) {
    chartOptions.series[1] = {
      targetAxisIndex: 0,
      color: '#e20015',
    }
    chartOptions.series[2] = {
      targetAxisIndex: 0,
      color: '#ffd700',
    }
    chartOptions.series[3] = {
      targetAxisIndex: 0,
      color: '#ff7f50',
    }
  }
  if (chartType === 'level') {
    return (
      <Grid container direction="column">
        <Grid container>
          {periods.map(({ label, value }) => (
            <Grid item key={value}>
              <Box
                p={0.25}
                style={{
                  border: '1px solid #555555',
                  cursor: 'pointer',
                  fontSize: '0.8em',
                  width: '50px',
                  backgroundColor: value === historyPeriod ? '#e20015' : '#fff',
                }}
                onClick={() => handlePeriodChange(value)}
              >
                <Typography variant="body1" color={value === historyPeriod ? 'primary' : 'secondary'}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Grid item>
          <Chart
            width={'100%'}
            height={500}
            loader={<div>Loading Chart</div>}
            chartType="LineChart"
            data={data}
            options={chartOptions}
            chartEvents={[
              {
                eventName: 'select',
                callback: ({ chartWrapper }) => {
                  const chart = chartWrapper.getChart()
                  const selection = chart.getSelection()
                  let noOfRecords;
                  if (selection.length === 1) {
                    const dataTable = chartWrapper.getDataTable()
                    noOfRecords = dataTable.getNumberOfRows();
                    var dateTime = dataTable.getValue(chart.getSelection()[0].row, 0)
                  }
                  let rowSelected = handleUsagePeriodChange(dateTime);
                  let arrSelectedRow = rowSelected.toString().split(',');
                  for (var i = 0; i < noOfRecords; i++) {
                    chartWrapper.getDataTable().setCell(i, 2, null);
                  }
                  if (arrSelectedRow[0] != undefined) {
                    chartWrapper.getDataTable().setCell(Number(arrSelectedRow[0]), 2, 'point {size: 4}');
                  }
                  if (arrSelectedRow[1] != undefined) {
                    chartWrapper.getDataTable().setCell(Number(arrSelectedRow[1]), 2, 'point {size: 4}');
                  }
                  chartWrapper.draw();
                },
              },
            ]}
          />
        </Grid>
        <Grid item>
          <Box my={{ xs: 2 }}>
            <Typography variant="body1" color="secondary">
              Usage Calculator
          </Typography>
          </Box>
        </Grid>
        <Grid container justify="space-between">
          <Grid item xs={12} sm={5}>
            <Grid container justify="space-between">
              <Grid item xs={1} sm={1}>
                <Radio
                  checked={optionSelected === 'fromDate'}
                  onChange={handleFocusChange}
                  value="fromDate"
                  name="radio-button-demo"
                  inputProps={{ 'aria-label': 'A' }}
                />
              </Grid>
              <Grid item xs={5} sm={11}>
                <TextField
                  label="From Date"
                  name="fromDate"
                  type="datetime-local"
                  InputProps={{
                    readOnly: true,
                  }}
                  value={isfromselected ? dateString(usagePeriod.from) : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Grid container justify="space-between">
              <Grid item xs={1} sm={1}>
                <Radio
                  checked={optionSelected === 'toDate'}
                  onChange={handleFocusChange}
                  value="toDate"
                  name="radio-button-demo"
                  inputProps={{ 'aria-label': 'A' }}
                />
              </Grid>
              <Grid item xs={5} sm={11}>
                <TextField
                  label="To Date"
                  name="toDate"
                  type="datetime-local"
                  value={istoselected ? dateString(usagePeriod.to) : ''}
                  onClick={handleFocusChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {(isfromselected && istoselected) && (tanktype !== 'gasmatic' ? (<Grid item>
          <Box my={{ xs: 2 }}>
            {!flowRate.error && (
              <Typography variant="body1" color="secondary">
                {flowRate.m3.toFixed(2)}m<sup>3</sup> / {flowRate.hours.toFixed(2)}h ({flowRate.ph.toFixed(2)}m
              <sup>3</sup>/h)
            </Typography>
            )}
            {flowRate.error && (
              <Typography variant="body1" color="secondary">
                {flowRate.error}
              </Typography>
            )}
          </Box>
        </Grid>) : (<Grid item>
          <Box my={{ xs: 2 }}>
            {!flowRate.error && (
              <Typography variant="body1" color="secondary">
                {flowRate.m3.toFixed(2)}kg / {flowRate.hours.toFixed(2)}h ({flowRate.ph.toFixed(2)}kg/h)
    </Typography>
            )}
            {flowRate.error && (
              <Typography variant="body1" color="secondary">
                {flowRate.error}
              </Typography>
            )}
          </Box>
        </Grid>))}

        <Grid item>
          <Box my={{ xs: 2 }}>
            <Typography variant="body1" color="secondary">
              Usage volumes calculated are indicative only.
          </Typography>
          </Box>
        </Grid>
      </Grid>
    )
  }
  else {
    return (
      <Grid container direction="column">
        <Grid container>
          {periods.map(({ label, value }) => (
            <Grid item key={value}>
              <Box
                p={0.25}
                style={{
                  border: '1px solid #555555',
                  cursor: 'pointer',
                  fontSize: '0.8em',
                  width: '50px',
                  backgroundColor: value === historyPeriod ? '#e20015' : '#fff',
                }}
                onClick={() => handlePeriodChange(value)}
              >
                <Typography variant="body1" color={value === historyPeriod ? 'primary' : 'secondary'}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Grid item>
          {(tanktype === 'bulk' || (tank.info !== undefined && tank.info.data !== undefined && tank.info.data.type == 'bulk')) && (<Chart
            width={'100%'}
            height={500}
            loader={<div>Loading Chart</div>}
            chartType="LineChart"
            data={dataPressure}
            options={chartOptionsPressureBulk}
          />)}
          {tanktype === 'pack' && (<Chart
            width={'100%'}
            height={500}
            loader={<div>Loading Chart</div>}
            chartType="LineChart"
            data={dataPressure}
            options={chartOptionsPressurePack}
          />)}
        </Grid>
      </Grid>
    )
  }
}
export default React.memo(History)
